import { useCallback } from "react";
import { useRecoilState } from "recoil";
import { Box } from "rebass/styled-components";

import { Avatar } from "../../AvatarV2";
import { FollowButton } from "../../FollowButton";
import { ModalType } from "../../../@types";
import { useMutateFollowers } from "../../../Hooks/useMutateFollowers";
import { useUser } from "../../../Hooks/useUser";
import { useIsFollowingUser } from "../../../Hooks/useIsFollowingUser";
import { AllstarModalState } from "../../../State/modals";
import { analytics } from "../../../Analytics";
import {
  PlayersList as PlayersListContainer,
  PlayerItem,
  PlayerRank,
  PlayerName,
  PlayerInfo,
  CustomFollowButton,
} from "../styledComponents";

import { ITopPlayer } from "MVVM/@types";

interface IPlayersListProps {
  topPlayers: ITopPlayer[];
}

interface IPlayerFollowButtonProps {
  player: ITopPlayer;
  handleFollow: (userId: string, player: ITopPlayer) => void;
  handleUnfollow: (userId: string) => void;
}

const PlayerFollowButton = ({
  player,
  handleFollow,
  handleUnfollow,
}: IPlayerFollowButtonProps) => {
  const isFollowing = useIsFollowingUser(player.internalId || undefined);

  return (
    <FollowButton
      isFollowing={isFollowing}
      onFollow={() => handleFollow(player.internalId!, player)}
      onUnfollow={() => handleUnfollow(player.internalId!)}
    />
  );
};

const PlayersList = ({ topPlayers }: IPlayersListProps) => {
  const { mutateFollowers } = useMutateFollowers();
  const { allstarUser } = useUser();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);

  const setFollowModal = useCallback(
    ({ displayName, avatar }: { displayName: string; avatar: string }) => {
      setAllstarModalState({
        ...allstarModalState,
        isOpen: ModalType.Signup,
        data: {
          target: displayName,
          avatar: avatar,
          action: "follow",
        },
      });
    },
    [allstarModalState, setAllstarModalState],
  );

  const handleFollow = useCallback(
    async (userId: string, player: ITopPlayer) => {
      if (!allstarUser?.loggedIn) {
        return setFollowModal({
          displayName: player.username,
          avatar: player.avatar || "",
        });
      }

      await mutateFollowers({
        variables: { userToFollow: userId, follow: true },
      });
      analytics.track("User Followed", {
        location: "top_5_widget",
        source: "players_list",
      });
    },
    [mutateFollowers, allstarUser?.loggedIn, setFollowModal],
  );

  const handleUnfollow = useCallback(
    async (userId: string) => {
      await mutateFollowers({
        variables: { userToFollow: userId, follow: false },
      });
      analytics.track("User Unfollowed", {
        location: "top_5_widget",
        source: "players_list",
      });
    },
    [mutateFollowers],
  );

  return (
    <PlayersListContainer>
      {topPlayers.map((player, index) => (
        <PlayerItem key={player.internalId} isFirst={index === 0}>
          <PlayerRank rank={index + 1}>{index + 1}</PlayerRank>
          <Box
            onClick={() =>
              (window.location.href = `/u/${
                player.username || player.internalId
              }`)
            }
            style={{ cursor: "pointer" }}
          >
            <Avatar
              src={player.avatar}
              size="sm"
              userGroup={player.userGroup?.type}
              rank={player.rank}
              profileCardUsername={player.username}
            />
          </Box>
          <PlayerInfo>
            <PlayerName
              onClick={() =>
                (window.location.href = `/u/${
                  player.username || player.internalId
                }`)
              }
              style={{ cursor: "pointer" }}
            >
              {player.username}
            </PlayerName>
          </PlayerInfo>
          <CustomFollowButton>
            <PlayerFollowButton
              player={player}
              handleFollow={handleFollow}
              handleUnfollow={handleUnfollow}
            />
          </CustomFollowButton>
        </PlayerItem>
      ))}
    </PlayersListContainer>
  );
};

export { PlayersList };
